/**
 * PaywallSheet — single canonical upgrade surface for the web app.
 *
 * Web doesn't process payments any more (see the breezy-volcano plan
 * for context: dating apps can't survive on mainstream payment
 * processors). Subscriptions happen exclusively in the iOS + Android
 * apps via Apple IAP / Google Play Billing, fronted by RevenueCat.
 *
 * Every premium-gated tap funnels through this ONE component so the
 * upgrade moment looks identical wherever it surfaces. Only the
 * headline + subtitle vary, keyed by `trigger` — the action the user
 * just tried to take. The supported triggers map 1:1 to entry points
 * in the product:
 *
 *   dm_open   — opened the DM inbox without premium
 *   dm_send   — tried to message someone from a public profile
 *   ai_review — tried to generate an AI review
 *   subscribe — generic upgrade tap (Home pitch, etc.)
 *   manage    — tapped Manage on the Settings plan badge. Different
 *               from `subscribe`: the user is asking how to change /
 *               cancel an existing plan (or sign up if free), not
 *               being pitched mid-task. Copy reflects that.
 *
 * Shape adapts to viewport:
 *   - ≤900px: bottom-sheet (slide-up, rounded top corners, full width)
 *   - >900px: centred modal capped at 480px wide
 *
 * Sized to fit in one glance on desktop without scrolling — no
 * redundant benefits card duplicating the subtitle, content kept
 * dense (hero → store handoff → fine print).
 *
 * Closes on scrim click + ESC.
 */
import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { APP_STORE_URL, GET_THE_APP_URL, PLAY_STORE_URL } from '@/lib/stores';
import { AppStoreBadge, PlayStoreBadge } from './StoreBadges';

export type PaywallTrigger = 'dm_open' | 'dm_send' | 'ai_review' | 'subscribe' | 'manage';

interface Props {
  open: boolean;
  trigger: PaywallTrigger;
  onClose: () => void;
}

// Trigger-keyed headline. Italics live inside <em> spans so the
// serif display font can mix roman + italic the way the type system
// expects. Tone scales the verb to the action: "Start", "Reply",
// "Generate", "Unlock" — each is what the user was literally just
// trying to do.
function headline(trigger: PaywallTrigger): React.ReactNode {
  switch (trigger) {
    case 'dm_open':   return (<><em>Read</em> your messages.</>);
    case 'dm_send':   return (<><em>Start</em> the conversation.</>);
    case 'ai_review': return (<><em>Generate</em> your AI take.</>);
    case 'manage':    return (<><em>Manage</em> in the app.</>);
    case 'subscribe':
    default:          return (<>Get <em>WYDM Premium.</em></>);
  }
}

// Trigger-keyed subtitle. Single sentence — the modal is one-glance.
// Each line names ONE benefit specific to the action the user took:
// reading DMs, sending DMs, AI reviews, or the whole bundle.
function subtitle(trigger: PaywallTrigger): string {
  switch (trigger) {
    case 'dm_open':
      return "See who's reached out, what they said, and reply — all premium-only.";
    case 'dm_send':
      return 'DMs are the only way to actually talk to someone who rated you.';
    case 'ai_review':
      return 'All 3 reviewers, unlimited reviews, re-roll until they get it right.';
    case 'manage':
      return 'Subscriptions live in the App Store and Play Store. Open WYDM on your phone to switch plan, update billing, or cancel.';
    case 'subscribe':
    default:
      return 'Unlimited AI reviews, full DMs, every rating in detail. One subscription.';
  }
}

export function PaywallSheet({ open, trigger, onClose }: Props) {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 901px)').matches,
  );

  // Track viewport breakpoint so the same component renders a centred
  // modal (desktop) vs bottom-sheet (mobile) without re-mounting.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 901px)');
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // ESC closes. Re-add per open-toggle so we don't leave handlers
  // attached after unmount.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Body scroll-lock while the sheet is open so the underlying page
  // doesn't scroll behind the scrim.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(43,29,20,0.5)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: isDesktop ? 'center' : 'flex-end',
        justifyContent: 'center',
        animation: 'fade-in 0.25s ease',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: isDesktop ? 480 : undefined,
          background: 'var(--bg)',
          borderRadius: isDesktop ? 28 : '32px 32px 0 0',
          // Tighter desktop padding so the whole modal lands in one
          // viewport — no benefits card to inflate the height now.
          padding: isDesktop ? '28px 28px 22px' : '14px 22px 24px',
          animation: isDesktop
            ? 'fade-in 0.25s ease'
            : 'slide-up 0.35s cubic-bezier(.2,.9,.3,1.1)',
          // Mobile keeps overflow auto for short viewports; desktop
          // is sized to fit so scrolling shouldn't trigger. The cap
          // is a safety net for very small windows or huge font
          // overrides, not the expected layout.
          maxHeight: isDesktop ? '90vh' : '90vh',
          overflowY: 'auto',
          boxShadow: isDesktop ? 'var(--shadow-card)' : undefined,
        }}
      >
        {!isDesktop && (
          <div
            aria-hidden
            style={{
              width: 40, height: 4,
              background: 'var(--line-strong)',
              borderRadius: 4,
              margin: '0 auto 16px',
            }}
          />
        )}

        {/* Hero — eyebrow, action-specific headline, single-sentence
            subtitle. No surface chrome; the type IS the design here. */}
        <div>
          <div
            className="mono"
            style={{
              fontSize: 11, color: 'var(--coral)',
              letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10,
            }}
          >
            WYDM Premium
          </div>
          <div
            className="serif"
            style={{
              fontSize: isDesktop ? 36 : 34,
              fontWeight: 500,
              lineHeight: 1.05,
              letterSpacing: -0.6,
              textWrap: 'balance' as React.CSSProperties['textWrap'],
            }}
          >
            {headline(trigger)}
          </div>
          <div
            style={{
              fontSize: 14.5,
              color: 'var(--ink-2)',
              marginTop: 12,
              lineHeight: 1.5,
              textWrap: 'balance' as React.CSSProperties['textWrap'],
            }}
          >
            {subtitle(trigger)}
          </div>
        </div>

        {/* Store handoff — the only conversion path on web. Mobile
            stacks the two badges; desktop puts them next to a QR so
            the user can scan with their phone without typing a URL. */}
        <GetTheAppSection isDesktop={isDesktop} />

        {/* Fine print — kept tiny + dim so it doesn't compete with
            the hero. Same line on every trigger; trigger-agnostic. */}
        <div
          style={{
            textAlign: 'center', fontSize: 11,
            color: 'var(--ink-3)', marginTop: 14, lineHeight: 1.5,
          }}
        >
          Subscriptions are managed in the App Store or Play Store.
          <div style={{ marginTop: 3 }}>
            <a href="/legal/terms" style={footerLink}>Terms</a>
            {' · '}
            <a href="/legal/privacy" style={footerLink}>Privacy</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// GetTheAppSection — the conversion surface. On mobile it shows the
// two store badges stacked (since the user already has a phone in
// hand, scanning a QR of their own screen is silly). On desktop the
// badges sit next to a QR code so the user can scan it with their
// phone and skip the "type wydm.app/get into your phone browser"
// step. SVG badges live in StoreBadges.tsx so they stay in lockstep
// with the /get redirect page's version.
function GetTheAppSection({ isDesktop }: { isDesktop: boolean }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: isDesktop ? 'row' : 'column',
        }}
      >
        {/* Store badges — stacked column on mobile (full width up to a
            sensible max), narrower side column on desktop next to the
            QR. */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          alignItems: 'stretch',
          width: isDesktop ? 148 : '100%',
          maxWidth: 240,
        }}>
          <AppStoreBadge href={APP_STORE_URL} height={44} />
          <PlayStoreBadge href={PLAY_STORE_URL} height={44} />
        </div>

        {/* QR — desktop only. Mobile users would be scanning their own
            screen with their own phone, which doesn't work. */}
        {isDesktop && (
          <div
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 4,
              padding: '8px 8px 6px',
              background: 'var(--surface)',
              borderRadius: 14,
              border: '1px solid var(--line)',
            }}
          >
            <QRCodeSVG
              value={GET_THE_APP_URL}
              size={96}
              bgColor="transparent"
              fgColor="var(--ink)"
              level="M"
            />
            <div
              className="mono"
              style={{
                fontSize: 9, color: 'var(--ink-3)',
                letterSpacing: 1, textTransform: 'uppercase',
              }}
            >
              Scan with phone
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const footerLink: React.CSSProperties = {
  color: 'var(--ink-3)',
  textDecoration: 'underline',
};
